declare const global: typeof globalThis & {
    __caches?: Record<string, unknown>;
};

class Cache<T> {
    private value!: T;

    constructor(
        initialValue: T | (() => T),
        private readonly options: {
            /** If this option is set, it allows the cache to persist through HMR (Hot Module Reload). */
            globalCacheID?: string;
        } = {},
    ) {
        if (process.env.NODE_ENV !== 'development') {
            delete this.options.globalCacheID;
        }

        if (options.globalCacheID && !('__caches' in globalThis)) {
            global.__caches = {};
        }
        const value: T = (() => {
            if (
                this.options.globalCacheID &&
                this.options.globalCacheID in Cache.GLOBAL() &&
                Cache.GLOBAL()[this.options.globalCacheID] !== undefined
            ) {
                return Cache.GLOBAL()[this.options.globalCacheID] as T;
            }

            return typeof initialValue === 'function' ? (initialValue as () => T)() : initialValue;
        })();

        this.set(value);
    }

    private static GLOBAL() {
        return global.__caches as Record<string, unknown>;
    }

    get(): T {
        return this.value;
    }

    set(value: T) {
        this.value = value;

        if (process.env.NODE_ENV === 'development' && this.options.globalCacheID) {
            Cache.GLOBAL()[this.options.globalCacheID] = value;
        }

        return this;
    }
}

export class RecordCache<
    T extends Record<PropertyKey, string | boolean | number | null | any[] | Record<any, any>>,
> {
    private cache: Partial<T>;

    constructor() {
        this.cache = {};
    }

    get<K extends keyof T>(key: K): T[K] | undefined {
        return this.cache[key];
    }

    getOrDefault<K extends keyof T>(key: K, defaultValue: T[K] | (() => T[K])): T[K] {
        if (!(key in this.cache)) {
            this.cache[key] = typeof defaultValue === 'function' ? defaultValue() : defaultValue;
        }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return this.get(key)!;
    }

    set<K extends keyof T>(key: K, value: T[K]) {
        this.cache[key] = value;
        return this;
    }
}

export class SingletonCache<T> {
    private cache: Cache<T>;

    constructor(...args: ConstructorParameters<typeof Cache<T>>) {
        this.cache = new Cache(...args);
    }

    get() {
        return this.cache.get();
    }

    /** Updates the value of the cache and retuns the old cache value */
    set(value: T) {
        const old = this.cache.get();
        this.cache.set(value);
        return old;
    }

    getOrDefault(init: () => T): T {
        const current = this.cache.get();
        if (current) return current;

        const value = init();
        this.set(value);
        return value;
    }
}

export class LazySingletonCache<T extends (...args: any[]) => Exclude<any, null>> {
    private cache: Cache<Awaited<ReturnType<T>> | null>;

    constructor(
        private readonly initFn: T,
        config: ConstructorParameters<typeof Cache<any>>[1] = {},
    ) {
        this.cache = new Cache(null, config);
    }

    get(...args: Parameters<T>): ReturnType<T> {
        const prev = this.cache.get();
        if (prev !== null) return prev;

        return this.init(...args);
    }

    init(...args: Parameters<T>): ReturnType<T> {
        const current = this.initFn(...args);
        if (current instanceof Promise) {
            return current.then(res => {
                this.cache.set(res);
                return res;
            }) as ReturnType<T>;
        }

        this.cache.set(current);
        return current;
    }
}
